import React, { useEffect, useState, useCallback } from "react";
import { supabase } from "../supabaseClient";
import {
  Box,
  Button,
  InputAdornment,
  Skeleton,
  Typography,
  TextField,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useTheme } from "@mui/material/styles";
import { logEvent } from "firebase/analytics";
import { analytics } from "../firebaseConfig";
import TeamFilter, { useTeamFilterParam } from "./TeamFilter";
import TeamCrest from "./ui/TeamCrest";
import ShareButton from "./ui/ShareButton";
import { seasonLabel } from "../utils/leagueCatalog";
import { fadeUp } from "./ui/motion";

const num = (v) => Number(v) || 0;

const sortByGoals = (rows) =>
  [...(rows || [])].sort(
    (a, b) =>
      num(b.goals) - num(a.goals) ||
      num(b.assists) - num(a.assists) ||
      String(a.name || "").localeCompare(String(b.name || ""))
  );

const MEDALS = ["#d4a017", "#9ea7b0", "#b8733c"];

const TeamLogo = ({ team, size = 28 }) => (
  <Box title={team?.name || ""} sx={{ display: "flex" }}>
    <TeamCrest team={team} size={size} />
  </Box>
);

const INITIAL_ROWS = 30;

const PODIUM_ORDER = [1, 0, 2];
const PEDESTAL = [{ xs: 92, sm: 120 }, { xs: 68, sm: 90 }, { xs: 52, sm: 70 }];

/** Golden Boot podium: 2nd, 1st, 3rd left to right, pedestals in medal colours. */
const Podium = ({ leaders }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", alignItems: "end", gap: { xs: 1, sm: 2 }, maxWidth: 560, mx: "auto" }}>
    {PODIUM_ORDER.map((rank) => {
      const p = leaders[rank];
      if (!p) return <Box key={rank} />;
      return (
        <Box key={p.id} sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", minWidth: 0, ...fadeUp(rank, 90) }}>
          <TeamCrest team={p.team} size={rank === 0 ? 56 : 44} />
          <Typography sx={{ mt: 1, fontSize: { xs: "0.82rem", sm: "0.95rem" }, fontWeight: 800, lineHeight: 1.2, color: "#111", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", wordBreak: "break-word" }}>
            {p.name}
          </Typography>
          <Typography noWrap sx={{ fontSize: "0.7rem", color: "#888", maxWidth: "100%" }}>
            {p.jersey_number != null ? `#${p.jersey_number} · ` : ""}
            {p.team?.name || ""}
          </Typography>
          <Box
            sx={{
              mt: 1,
              width: "100%",
              height: PEDESTAL[rank],
              borderRadius: "14px 14px 4px 4px",
              background: `linear-gradient(180deg, ${MEDALS[rank]} 0%, ${MEDALS[rank]}cc 100%)`,
              color: "#fff",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "flex-start",
              pt: 1,
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.4)",
            }}
          >
            <Typography sx={{ fontSize: { xs: "1.6rem", sm: "2rem" }, fontWeight: 900, lineHeight: 1, fontVariantNumeric: "tabular-nums", textShadow: "0 1px 2px rgba(0,0,0,0.2)" }}>
              {num(p.goals)}
            </Typography>
            <Typography sx={{ fontSize: "0.6rem", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", opacity: 0.85 }}>
              {rank === 0 ? "Golden Boot" : num(p.goals) === 1 ? "goal" : "goals"}
            </Typography>
          </Box>
        </Box>
      );
    })}
  </Box>
);

const RedCard = () => (
  <Box
    component="span"
    sx={{ display: "inline-block", width: 8, height: 11, borderRadius: "1.5px", backgroundColor: "#d32f2f" }}
  />
);

const Stats = ({
  city,
  division,
  sport = "Soccer",
  ageGroup = "Adult",
  /** When true, load `youth_players` / `youth_teams` (ignore city, division, sport, ageGroup). */
  youthMode = false,
  season = null,
  showTitle = true,
}) => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [teamOptions, setTeamOptions] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useTeamFilterParam();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  /** Teams playing in the current city / division / sport, from their fixtures. */
  const fetchDivisionTeamIds = useCallback(async () => {
    let q = supabase
      .from("fixtures")
      .select("home_team_id, away_team_id")
      .eq("city", city)
      .eq("sport", sport)
      .eq("age_group", ageGroup)
      .ilike("division", division);
    if (season) q = q.eq("season", season);
    const { data, error } = await q;

    if (error) throw error;

    return Array.from(
      new Set(
        (data || [])
          .flatMap((f) => [f.home_team_id, f.away_team_id])
          .filter(Boolean)
      )
    );
  }, [city, division, sport, ageGroup, season]);

  const fetchStats = useCallback(
    async (search = "", teamId = "") => {
      try {
        if (youthMode) {
          let q = supabase
            .from("youth_players")
            .select(
              "id, name, jersey_number, goals, assists, team_id, team:youth_teams!team_id(name, logo_url)"
            );
          if (teamId) q = q.eq("team_id", teamId);
          if (search) {
            q = q.ilike("name", `%${search}%`);
            logEvent(analytics, "player_search", { searchTerm: search });
          }
          const { data, error } = await q;
          if (error) throw error;
          setPlayers(sortByGoals(data));
          return;
        }

        let teamIds = null;

        if (division) {
          teamIds = await fetchDivisionTeamIds();

          if (teamIds.length === 0) {
            setPlayers([]);
            return;
          }
        }

        let q = supabase
          .from("players")
          .select(
            "id, name, jersey_number, goals, assists, red_cards, team_id, team:team_id(name, logo_url)"
          )
          .eq("city", city)
          .eq("sport", sport)
          .eq("age_group", ageGroup);

        if (teamIds) q = q.in("team_id", teamIds);
        if (teamId) q = q.eq("team_id", teamId);

        if (search) {
          q = q.ilike("name", `%${search}%`);
          logEvent(analytics, "player_search", { searchTerm: search });
        }

        const { data, error } = await q;
        if (error) throw error;

        setPlayers(sortByGoals(data));
      } catch (error) {
        console.error("Error fetching stats:", error);
        setPlayers([]);
      }
    },
    [city, division, sport, ageGroup, youthMode, fetchDivisionTeamIds]
  );

  useEffect(() => {
    let cancelled = false;

    const fetchTeamOptions = async () => {
      try {
        if (youthMode) {
          const { data, error } = await supabase
            .from("youth_teams")
            .select("id, name, logo_url")
            .order("name");
          if (error) throw error;
          if (!cancelled) setTeamOptions(data || []);
          return;
        }

        let q = supabase
          .from("teams")
          .select("id, name, logo_url")
          .eq("city", city)
          .eq("sport", sport)
          .order("name");

        if (division) {
          const teamIds = await fetchDivisionTeamIds();
          if (teamIds.length === 0) {
            if (!cancelled) setTeamOptions([]);
            return;
          }
          q = q.in("id", teamIds);
        }

        const { data, error } = await q;
        if (error) throw error;
        if (!cancelled) setTeamOptions(data || []);
      } catch (error) {
        console.error("Error fetching teams:", error);
        if (!cancelled) setTeamOptions([]);
      }
    };

    fetchTeamOptions();
    return () => {
      cancelled = true;
    };
  }, [city, division, sport, youthMode, fetchDivisionTeamIds]);

  const isTeamFiltered = teamOptions.some(
    (t) => String(t.id) === String(selectedTeamId)
  );

  const activeTeamId = isTeamFiltered ? selectedTeamId : "";

  /** Drop a team that isn't in this city / division (e.g. after a city change). */
  useEffect(() => {
    if (selectedTeamId && teamOptions.length > 0 && !isTeamFiltered) {
      setSelectedTeamId("");
    }
  }, [selectedTeamId, teamOptions.length, isTeamFiltered, setSelectedTeamId]);

  useEffect(() => {
    setSearchTerm("");
    setShowAll(false);
    setLoading(true);
    fetchStats("", activeTeamId).finally(() => setLoading(false));
  }, [fetchStats, activeTeamId]);

  const handleSearch = useCallback(
    (event) => {
      const value = event.target.value;
      setSearchTerm(value);

      clearTimeout(window.searchTimeout);
      window.searchTimeout = setTimeout(() => {
        fetchStats(value, activeTeamId);
      }, 300);
    },
    [fetchStats, activeTeamId]
  );

  const showRedCards = !youthMode;
  const topScorers = players.filter((p) => num(p.goals) > 0).slice(0, 5);
  const topIds = new Set(topScorers.map((p) => p.id));
  const selectedTeam = teamOptions.find((t) => String(t.id) === String(activeTeamId));
  const maxGoals = Math.max(0, ...players.map((p) => num(p.goals)));

  const statCols = [
    { key: "goals", label: "G", strong: true },
    { key: "assists", label: "A" },
    ...(isMobile ? [] : [{ key: "ga", label: "G+A" }]),
    ...(showRedCards ? [{ key: "red_cards", label: "RC" }] : []),
  ];
  const gridCols = `36px minmax(0, 1fr) 40px ${statCols.map(() => (isMobile ? "36px" : "52px")).join(" ")}`;

  const cellNum = (player, key) =>
    key === "ga" ? num(player.goals) + num(player.assists) : num(player[key]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        py: 3,
        px: isMobile ? 1.5 : 3,
        pb: isMobile ? 20 : 4,
      }}
    >
      {showTitle && (
        <Typography sx={{ fontSize: isMobile ? "1.6rem" : "2rem", fontWeight: 800, color: "#1a1a1a", mb: 2 }}>
          Player Stats
        </Typography>
      )}

      <Box sx={{ mb: 2, display: "flex", justifyContent: "center", width: "100%" }}>
        <TeamFilter teams={teamOptions} value={selectedTeamId} onChange={setSelectedTeamId} allLabel="All teams" />
      </Box>

      <Box sx={{ mb: 3, width: "100%", maxWidth: 600 }}>
        <TextField
          variant="outlined"
          placeholder="Search players by jersey name"
          value={searchTerm}
          onChange={handleSearch}
          fullWidth
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9e9e9e" }} />
              </InputAdornment>
            ),
          }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 999, backgroundColor: "#fff" } }}
        />
      </Box>

      {topScorers.length > 0 && !searchTerm && !loading && (
        <Box sx={{ width: "100%", maxWidth: 900, minWidth: 0, mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, mb: 1.5 }}>
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 800, letterSpacing: "0.12em", color: "#8a8a8a", textTransform: "uppercase" }}>
              Top scorers
            </Typography>
            <ShareButton
              kind="scorers"
              variant="dark"
              label="Share top 5"
              sx={{ py: 0.25 }}
              data={{
                title: "Top scorers",
                subtitle: youthMode
                  ? "HSL Youth"
                  : [city, division, season ? seasonLabel(season) : null, selectedTeam?.name].filter(Boolean).join(" · "),
                players: topScorers,
                division: youthMode ? null : division,
                city: youthMode ? null : city,
              }}
            />
          </Box>
          <Podium leaders={topScorers.slice(0, 3)} />
        </Box>
      )}

      <Box
        sx={{
          width: "100%",
          maxWidth: 900,
          backgroundColor: "#fff",
          borderRadius: 4,
          border: "1px solid #ececec",
          boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 4px 14px rgba(16,24,40,0.05)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: gridCols,
            alignItems: "center",
            columnGap: 1,
            px: 1.5,
            py: 1.25,
            backgroundColor: "#fafafa",
            borderBottom: "1px solid #f0f0f0",
            fontSize: "0.7rem",
            fontWeight: 800,
            letterSpacing: "0.08em",
            color: "#8a8a8a",
            textTransform: "uppercase",
          }}
        >
          <Box sx={{ textAlign: "center" }}>#</Box>
          <Box>Player</Box>
          <Box sx={{ textAlign: "center" }}>Team</Box>
          {statCols.map((c) => (
            <Box key={c.key} sx={{ textAlign: "center", color: c.strong ? "#1a1a1a" : undefined }}>
              {c.label}
            </Box>
          ))}
        </Box>

        {loading ? (
          <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} variant="rounded" height={36} />
            ))}
          </Box>
        ) : players.length === 0 ? (
          <Box sx={{ py: 4, px: 2, textAlign: "center" }}>
            <Typography sx={{ fontWeight: 800, color: "#111" }}>
              {searchTerm ? `No players match “${searchTerm}”` : "No player stats yet"}
            </Typography>
            <Typography sx={{ mt: 0.5, color: "#8a8a8a", fontSize: "0.88rem" }}>
              {searchTerm ? "Try a shorter name or clear the search." : "Goals and assists show up here once games are played."}
            </Typography>
          </Box>
        ) : (
          (showAll || searchTerm ? players : players.slice(0, INITIAL_ROWS)).map((player, index) => {
            const isTop = topIds.has(player.id);
            return (
              <Box
                key={player.id != null ? player.id : index}
                sx={{
                  display: "grid",
                  gridTemplateColumns: gridCols,
                  alignItems: "center",
                  columnGap: 1,
                  px: 1.5,
                  py: 1,
                  borderBottom: "1px solid #f4f4f4",
                  "&:last-of-type": { borderBottom: "none" },
                  backgroundColor: isTop ? "rgba(212,160,23,0.06)" : "transparent",
                  boxShadow: isTop ? "inset 3px 0 0 #d4a017" : "none",
                }}
              >
                <Box sx={{ textAlign: "center", fontSize: "0.8rem", fontWeight: 700, color: isTop ? "#b8860b" : "#9e9e9e" }}>
                  {index + 1}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: "0.9rem", fontWeight: isTop ? 700 : 600, color: "#1a1a1a" }}>
                    {player.name}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.25 }}>
                    <Typography component="span" sx={{ fontSize: "0.7rem", color: "#9e9e9e", fontVariantNumeric: "tabular-nums", minWidth: 24 }}>
                      {player.jersey_number != null && player.jersey_number !== "" ? `#${player.jersey_number}` : "—"}
                    </Typography>
                    {maxGoals > 0 && num(player.goals) > 0 && (
                      <Box sx={{ flex: 1, maxWidth: 120, height: 4, borderRadius: 2, bgcolor: "#f1f1f1", overflow: "hidden" }}>
                        <Box sx={{ width: `${(num(player.goals) / maxGoals) * 100}%`, height: "100%", borderRadius: 2, bgcolor: isTop ? "#d4a017" : "#a1a1aa" }} />
                      </Box>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <TeamLogo team={player.team} />
                </Box>
                {statCols.map((c) => {
                  const v = cellNum(player, c.key);
                  if (c.key === "red_cards") {
                    return (
                      <Box key={c.key} sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                        {v > 0 ? (
                          <>
                            <RedCard />
                            <Typography component="span" sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#d32f2f" }}>
                              {v}
                            </Typography>
                          </>
                        ) : (
                          <Typography component="span" sx={{ fontSize: "0.85rem", color: "#c4c4c4" }}>0</Typography>
                        )}
                      </Box>
                    );
                  }
                  return (
                    <Typography
                      key={c.key}
                      sx={{
                        textAlign: "center",
                        fontSize: c.strong ? "0.95rem" : "0.85rem",
                        fontWeight: c.strong ? 800 : 500,
                        fontVariantNumeric: "tabular-nums",
                        color: v === 0 ? "#c4c4c4" : "#1a1a1a",
                      }}
                    >
                      {v}
                    </Typography>
                  );
                })}
              </Box>
            );
          })
        )}
      </Box>
      {!loading && !showAll && !searchTerm && players.length > INITIAL_ROWS && (
        <Button
          onClick={() => setShowAll(true)}
          sx={{ mt: 2, fontWeight: 800, color: "#111", borderRadius: 999, px: 3, border: "1px solid #e4e4e7", bgcolor: "#fff", "&:hover": { bgcolor: "#f4f4f5" } }}
        >
          Show all {players.length} players
        </Button>
      )}
    </Box>
  );
};

export default Stats;
